import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense} from 'react'
import {useStableRandom} from '@github-ui/use-stable-random'
import {ForYouItemSkeleton} from './shared/ForYouItemFallbacks'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import styles from './shared/ForYouSubSection.module.css'
import {useDecideNextAction} from './deciders/use-decide-next-action'
import {useViewerRoles} from './shared/use-viewer-roles'
import {useReviewRequestedData} from './reviewer/use-review-requested-data'
import {clsx} from 'clsx'

export function ForYouNextAction() {
  const {queryStatus: viewerRolesQueryStatus} = useViewerRoles()
  const {queryStatus: reviewRequestsQueryStatus} = useReviewRequestedData()
  const recommendedAction = useDecideNextAction()

  if (viewerRolesQueryStatus === 'loading' || reviewRequestsQueryStatus === 'loading') {
    return <ForYouNextActionSkeleton />
  }

  if (viewerRolesQueryStatus !== 'success' || reviewRequestsQueryStatus !== 'success' || recommendedAction === null) {
    return null
  }

  // since we're re-using the For You implementation, we need to wrap the list of items in a <ul>
  // so that the <li> elements inside ForYouItem are valid for a11y.
  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <ul role="list" className={clsx(styles.list, styles.CondensedForYouList)}>
      {recommendedAction}
    </ul>
  )
}

export function ForYouNextActionBoundary({children}: {children: React.ReactNode}) {
  return (
    <ErrorBoundary boundaryName="ForYouNextActionBoundary" fallback={null}>
      <Suspense fallback={<ForYouNextActionSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

function ForYouNextActionSkeleton() {
  const phraseIndex = useStableRandom({min: 0, max: LOADING_PHRASES.length - 1})
  const phrase = LOADING_PHRASES[phraseIndex]

  return (
    <ul className={clsx(styles.list, styles.CondensedForYouList)}>
      <ForYouItemSkeleton
        text={phrase}
        variant="embedded"
        action={<LoadingSkeleton variant="rounded" width="120px" height="28px" />}
      />
    </ul>
  )
}

const LOADING_PHRASES = [
  "Deserializing your pull request's destiny…",
  'git blame-ing the delay…',
  'Traversing the commit graph…',
  'Reticulating CI pipelines…',
  'Garbage collecting stale opinions…',
  'Checking if the dog ate your approvals…',
  "Waiting for Schrödinger's CI to collapse into a state…",
  'Begging the automerge daemon for a straight answer…',
  'Asking git log --oneline for the meaning of life…',
  'Checking out your fate on a detached HEAD…',
]
